import React, { useState } from "react";
import { useParams } from "react-router-dom";

import firebase from "firebaseFactory";
import { Page } from "modules/layout/Page";
import { useFirebaseFunction, useCachedAsyncData } from "hooks";
import {
  fetchAthenaResults,
  AthenaResultsSource,
} from "components/athena/FetchAthenaResults";
import { HighchartsFromAthenaResults } from "components/highcharts/HighchartsFromAthenaResults";
import { Heading2 } from "components/Heading2";
import {
  TableRow,
  Table,
  TableCell,
  TableHeaderCell,
  TableBody,
} from "components/Table";
import { ExternalLinkButton } from "components/ExternalLinkButton";

export const EventDetailsPage = ({
  getEventData = firebase.function("getEventData"),
  getWaveformData = firebase.function("getWaveformData"),
}) => {
  const { siteId, deviceId, unitId, eventTimestamp } = useParams();
  const [waveformDownloadUrl, setWaveformDownloadUrl] = useState<string>();
  const { data: athenaWaveformQuery } = useFirebaseFunction<
    AthenaResultsSource
  >(
    getWaveformData,
    { siteId, deviceId, unitId, waveformTimestamp: eventTimestamp },
    { shouldRevalidateData: false }
  );
  const { data: athenaEventQuery } = useFirebaseFunction<AthenaResultsSource>(
    getEventData,
    { siteId, deviceId, unitId, eventTimestamp: eventTimestamp },
    { shouldRevalidateData: false }
  );

  const { data: athenaEventResults } = useCachedAsyncData(
    async () =>
      athenaEventQuery &&
      (await fetchAthenaResults(athenaEventQuery, {
        onStatusUpdate: console.log,
      })),
    athenaEventQuery?.headUrl || null,
    { initialData: undefined, shouldRevalidateData: false }
  );

  const eventData =
    !athenaEventResults || athenaEventResults.rawRows.length === 0
      ? {}
      : Object.fromEntries(
          athenaEventResults?.columns.map((x) => [
            x.name,
            athenaEventResults.rawRows[0][x.index],
          ])
        );

  return (
    <Page>
      <div className="mb-6">
        <div className="flex justify-between mb-2">
          <Heading2>Event Summary</Heading2>
        </div>
        <div>
          {!athenaEventResults ? (
            "Loading event data..."
          ) : !eventData ? (
            "No event found"
          ) : (
            <Table>
              <TableBody>
                <TableRow>
                  <TableHeaderCell>Timestamp</TableHeaderCell>
                  <TableCell>{eventData["timestamp_utc"]}</TableCell>
                </TableRow>
                <TableRow>
                  <TableHeaderCell>event_code</TableHeaderCell>
                  <TableCell>{eventData["event_code"]}</TableCell>
                </TableRow>
                <TableRow>
                  <TableHeaderCell>event_text</TableHeaderCell>
                  <TableCell>{eventData["event_text"]}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </div>
      </div>
      <div className="mb-6">
        <div className="flex justify-between mb-2">
          <Heading2>Waveform Data</Heading2>
          {waveformDownloadUrl && (
            <ExternalLinkButton href={waveformDownloadUrl}>
              Download CSV
            </ExternalLinkButton>
          )}
        </div>
        {athenaWaveformQuery ? (
          <>
            <HighchartsFromAthenaResults
              athenaResults={athenaWaveformQuery}
              onComplete={({ url }: { url: string }) =>
                setWaveformDownloadUrl(url)
              }
            />
          </>
        ) : (
          <div>Loading waveform data...</div>
        )}
      </div>
    </Page>
  );
};
